import * as THREE from 'three';

// Constellation name labels for the AI Mind view — planetarium-style.
// Text sprites hover above cluster centroids, attached as children of the
// stars Points object so they rotate with the map. Visible only in the
// 'mind' view; opacity eases in/out on view switches. Distant labels get a
// minimum angular size so far constellations stay readable.

export interface MindLabel {
  name: string;
  count: number;
  position: { x: number; y: number; z: number };
}

const FADE_SPEED = 1.6; // opacity units per second
const MAX_OPACITY = 0.92;
const MIN_WIDTH_PER_DIST = 0.06; // min apparent size: world units per unit distance
const GROWTH_CAP = 2.4; // far labels grow at most this much over base width

const makeTextTexture = (
  text: string,
  anisotropy: number
): { texture: THREE.Texture; aspect: number } => {
  const canvas = document.createElement('canvas');
  canvas.width = 2048;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.font = "700 118px 'Rajdhani', sans-serif";
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const cx = canvas.width / 2;
    const cy = canvas.height / 2 + 6;
    const label = text.toUpperCase();
    // Dark outline for crispness, light glow, then bright fill
    ctx.lineWidth = 10;
    ctx.strokeStyle = 'rgba(0, 14, 28, 0.9)';
    ctx.strokeText(label, cx, cy);
    ctx.shadowColor = 'rgba(0, 204, 255, 0.75)';
    ctx.shadowBlur = 10;
    ctx.fillStyle = 'rgba(215, 240, 255, 0.98)';
    ctx.fillText(label, cx, cy);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.anisotropy = anisotropy;
  texture.needsUpdate = true;
  return { texture, aspect: canvas.width / canvas.height };
};

interface LabelItem {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  baseWidth: number;
  aspect: number;
}

export class MindLabelsSystem {
  private group: THREE.Group | null = null;
  private items: LabelItem[] = [];
  private targetVisible = false;
  private opacity = 0;
  private tmp = new THREE.Vector3();

  setLabels(parent: THREE.Object3D, labels: MindLabel[], anisotropy: number = 4): void {
    this.disposeGroup();
    this.group = new THREE.Group();
    this.items = [];

    const maxCount = labels.reduce((m, l) => Math.max(m, l.count), 1);
    for (const label of labels) {
      const { texture, aspect } = makeTextTexture(label.name, anisotropy);
      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        opacity: 0,
        depthTest: false,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(material);
      // Bigger clusters get bigger labels (13..22 world units wide)
      const baseWidth = 13 + 9 * Math.sqrt(label.count / maxCount);
      sprite.scale.set(baseWidth, baseWidth / aspect, 1);
      sprite.position.set(label.position.x, label.position.y, label.position.z);
      sprite.renderOrder = 999;
      this.group.add(sprite);
      this.items.push({ sprite, material, baseWidth, aspect });
    }

    parent.add(this.group);
    this.group.visible = this.opacity > 0.01;
  }

  setVisible(visible: boolean): void {
    this.targetVisible = visible;
  }

  tick(dt: number, camera?: THREE.Camera): void {
    if (!this.group) return;
    const target = this.targetVisible ? MAX_OPACITY : 0;
    if (Math.abs(this.opacity - target) < 0.01) {
      this.opacity = target;
    } else {
      const dir = target > this.opacity ? 1 : -1;
      this.opacity = Math.min(Math.max(this.opacity + dir * FADE_SPEED * dt, 0), MAX_OPACITY);
    }
    const show = this.opacity > 0.01;
    if (this.group.visible !== show) this.group.visible = show;
    if (!show) return;

    for (const item of this.items) {
      item.material.opacity = this.opacity;
      if (camera) {
        // Keep far labels readable: enforce a minimum apparent size
        item.sprite.getWorldPosition(this.tmp);
        const dist = camera.position.distanceTo(this.tmp);
        const minWidth = dist * MIN_WIDTH_PER_DIST;
        const width =
          item.baseWidth < minWidth
            ? Math.min(minWidth, item.baseWidth * GROWTH_CAP)
            : item.baseWidth;
        item.sprite.scale.set(width, width / item.aspect, 1);
      }
    }
  }

  private disposeGroup(): void {
    if (!this.group) return;
    this.group.parent?.remove(this.group);
    for (const item of this.items) {
      item.material.map?.dispose();
      item.material.dispose();
    }
    this.group = null;
    this.items = [];
  }

  destroy(): void {
    this.disposeGroup();
  }
}
